/**
 * Socket Health Monitor
 * Tracks connection health, implements exponential backoff, and provides recovery strategies
 */

import { socketService } from './SocketService';
import type { SocketNamespace } from '@/types';

interface ConnectionHealth {
  namespace: SocketNamespace;
  connected: boolean;
  lastConnected: number | null;
  lastDisconnected: number | null;
  reconnectAttempts: number;
  failureCount: number;
  isHealthy: boolean;
}

class SocketHealthMonitor {
  private healthMap: Map<SocketNamespace, ConnectionHealth> = new Map();
  private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
  private readonly HEALTH_CHECK_INTERVAL = 30000; // 30 seconds
  private readonly MAX_FAILURE_THRESHOLD = 5;
  private readonly RECOVERY_COOLDOWN = 60000; // 1 minute

  /**
   * Initialize health monitoring for a namespace
   */
  track(namespace: SocketNamespace): void {
    if (!this.healthMap.has(namespace)) {
      this.healthMap.set(namespace, {
        namespace,
        connected: false,
        lastConnected: null,
        lastDisconnected: null,
        reconnectAttempts: 0,
        failureCount: 0,
        isHealthy: true,
      });
    }

    this.startHealthChecks();
  }

  /**
   * Record a successful connection
   */
  recordConnection(namespace: SocketNamespace): void {
    const health = this.healthMap.get(namespace);
    if (health) {
      health.connected = true;
      health.lastConnected = Date.now();
      health.reconnectAttempts = 0;
      health.failureCount = 0;
      health.isHealthy = true;
    }
  }

  /**
   * Record a disconnection
   */
  recordDisconnection(namespace: SocketNamespace, reason: string): void {
    const health = this.healthMap.get(namespace);
    if (health) {
      health.connected = false;
      health.lastDisconnected = Date.now();

      // Increment failure count for certain disconnect reasons
      if (reason === 'io server disconnect' || reason === 'transport error') {
        health.failureCount++;
      }

      // Mark as unhealthy if too many failures
      if (health.failureCount >= this.MAX_FAILURE_THRESHOLD) {
        health.isHealthy = false;
        console.warn(`⚠️ Namespace /${namespace} marked as unhealthy after ${health.failureCount} failures`);
        this.scheduleRecovery(namespace);
      }
    }
  }

  /**
   * Record a reconnection attempt
   */
  recordReconnectAttempt(namespace: SocketNamespace): void {
    const health = this.healthMap.get(namespace);
    if (health) {
      health.reconnectAttempts++;
    }
  }

  /**
   * Get health status for a namespace
   */
  getHealth(namespace: SocketNamespace): ConnectionHealth | null {
    return this.healthMap.get(namespace) || null;
  }

  /**
   * Get overall system health
   */
  getOverallHealth(): {
    healthy: number;
    unhealthy: number;
    disconnected: number;
    total: number;
  } {
    let healthy = 0;
    let unhealthy = 0;
    let disconnected = 0;

    this.healthMap.forEach((health) => {
      if (health.isHealthy && health.connected) {
        healthy++;
      } else if (!health.isHealthy) {
        unhealthy++;
      } else if (!health.connected) {
        disconnected++;
      }
    });

    return {
      healthy,
      unhealthy,
      disconnected,
      total: this.healthMap.size,
    };
  }

  /**
   * Schedule recovery attempt for unhealthy namespace
   */
  private scheduleRecovery(namespace: SocketNamespace): void {
    console.log(`🔧 Scheduling recovery for /${namespace} in ${this.RECOVERY_COOLDOWN}ms`);

    setTimeout(() => {
      const health = this.healthMap.get(namespace);
      if (health && !health.isHealthy) {
        console.log(`🔄 Attempting recovery for /${namespace}...`);
        this.attemptRecovery(namespace);
      }
    }, this.RECOVERY_COOLDOWN);
  }

  /**
   * Attempt to recover an unhealthy connection
   */
  private attemptRecovery(namespace: SocketNamespace): void {
    const health = this.healthMap.get(namespace);
    if (!health) return;

    console.log(`🔧 Attempting recovery for /${namespace}...`);

    try {
      // Disconnect existing socket
      socketService.disconnect(namespace);

      // Reset health counters
      health.failureCount = 0;
      health.reconnectAttempts = 0;
      health.isHealthy = true;

      // Wait a bit before reconnecting (exponential backoff)
      const backoffDelay = Math.min(2000 * Math.pow(2, health.reconnectAttempts), 30000);

      setTimeout(async () => {
        try {
          await socketService.connect(namespace);
          console.log(`✅ Recovery initiated for /${namespace} (delay: ${backoffDelay}ms)`);
        } catch (error) {
          console.error(`❌ Recovery failed for /${namespace}:`, error);
          health.isHealthy = false;
          health.reconnectAttempts++;

          // Only retry if attempts < 5
          if (health.reconnectAttempts < 5) {
            this.scheduleRecovery(namespace);
          } else {
            console.error(`❌ Max recovery attempts reached for /${namespace}`);
          }
        }
      }, backoffDelay);
    } catch (error) {
      console.error(`❌ Error during recovery for /${namespace}:`, error);
    }
  }

  /**
   * Start periodic health checks
   */
  private startHealthChecks(): void {
    if (this.healthCheckInterval) {
      console.log('🏥 Socket health monitoring already running');
      return;
    }

    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, this.HEALTH_CHECK_INTERVAL);

    console.log('🏥 Socket health monitoring started (interval: 30s)');
  }

  /**
   * Perform health check on all tracked namespaces
   */
  private performHealthCheck(): void {
    const overallHealth = this.getOverallHealth();

    // Log if there are any issues
    if (overallHealth.unhealthy > 0 || overallHealth.disconnected > 0) {
      console.log('🏥 Health Check:', overallHealth);
    }

    // Check for stale connections (disconnected for > 5 minutes)
    const now = Date.now();
    this.healthMap.forEach((health, namespace) => {
      if (
        !health.connected &&
        health.lastDisconnected &&
        now - health.lastDisconnected > 300000 // 5 minutes
      ) {
        console.warn(
          `⚠️ Namespace /${namespace} has been disconnected for ${Math.floor((now - health.lastDisconnected) / 1000)}s`
        );

        // Attempt reconnection for stale connections
        if (health.isHealthy) {
          this.attemptRecovery(namespace);
        }
      }
    });
  }

  /**
   * Stop health monitoring
   */
  stop(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
      console.log('🏥 Socket health monitoring stopped');
    }
  }

  /**
   * Reset health monitoring for a namespace
   */
  reset(namespace: SocketNamespace): void {
    const health = this.healthMap.get(namespace);
    if (health) {
      health.failureCount = 0;
      health.reconnectAttempts = 0;
      health.isHealthy = true;
      health.connected = socketService.isConnected(namespace);
    }
  }

  /**
   * Get diagnostic information
   */
  getDiagnostics(): string {
    const lines: string[] = ['=== Socket Health Diagnostics ==='];
    const overall = this.getOverallHealth();

    lines.push(`\nOverall: ${overall.healthy}/${overall.total} healthy`);
    lines.push(`Unhealthy: ${overall.unhealthy}`);
    lines.push(`Disconnected: ${overall.disconnected}\n`);

    this.healthMap.forEach((health, namespace) => {
      lines.push(`/${namespace}:`);
      lines.push(`  Status: ${health.connected ? '✅' : '❌'} ${health.isHealthy ? '(healthy)' : '(unhealthy)'}`);
      lines.push(`  Failures: ${health.failureCount}`);
      lines.push(`  Reconnect Attempts: ${health.reconnectAttempts}`);

      if (health.lastConnected) {
        const connectedAgo = Math.floor((Date.now() - health.lastConnected) / 1000);
        lines.push(`  Last Connected: ${connectedAgo}s ago`);
      }

      if (health.lastDisconnected) {
        const disconnectedAgo = Math.floor((Date.now() - health.lastDisconnected) / 1000);
        lines.push(`  Last Disconnected: ${disconnectedAgo}s ago`);
      }

      lines.push('');
    });

    return lines.join('\n');
  }
}

export const socketHealthMonitor = new SocketHealthMonitor();

// Expose diagnostics to window for debugging
if (typeof window !== 'undefined') {
  (window as any).getSocketHealth = () => {
    console.log(socketHealthMonitor.getDiagnostics());
    return socketHealthMonitor.getOverallHealth();
  };
}
